import { useContext, useEffect, useRef, useState } from 'react';
import * as turf from '@turf/turf';
import mapboxgl from 'mapbox-gl';
import MapContext from '../../context/MapContext';
import UIContext from '../../context/UIContext';
import ReportIssueControl from '../../utils/mapbox/reportIssueControl';
import UserReportPopup from './UserReportPopup';
import UserReportedBathymetryPopup from './UserReportedBathymetryPopup';
import {
    draftReportToGeoJson,
    formatUserReportTime,
    REPORT_CATEGORY_BY_ID,
    reloadUserReportsSource,
    getUserReportDescription,
    USER_REPORT_MIN_ZOOM,
    userReportFillPaint,
    userReportLabelLayout,
    userReportLabelPaint,
    userReportOuterPaint,
    userReportShadowPaint,
    userReportWhitePaint,
} from '../../utils/userReports';

const USER_REPORT_HIT_LAYER_IDS = [
    'user_reports_shadow',
    'user_reports_outer',
    'user_reports_white',
    'user_reports_fill',
    'user_reports_1',
];

const BATHYMETRY_HIT_LAYER_IDS = [
    'user_reported_bathymetry_shadow',
    'user_reported_bathymetry_line',
];

const HIT_LAYER_IDS = [
    ...USER_REPORT_HIT_LAYER_IDS,
    ...BATHYMETRY_HIT_LAYER_IDS,
];

const BATHYMETRY_HIT_LAYER_SET = new Set(BATHYMETRY_HIT_LAYER_IDS);

function formatLengthMeters(lengthM) {
    const n = Number(lengthM);
    if (!Number.isFinite(n) || n < 0) {
        return '';
    }
    if (n >= 1000) {
        const km = n / 1000;
        const rounded = km >= 10 ? km.toFixed(0) : km.toFixed(1);
        return rounded + ' km';
    }
    return Math.round(n) + ' m';
}

function parseDepthMeters(depth) {
    const n = Number(depth);
    return Number.isFinite(n) ? n : null;
}

function isBathymetryFeature(feature) {
    return BATHYMETRY_HIT_LAYER_SET.has(feature?.layer?.id);
}

// MVT often encodes numeric props as strings; coerce epoch ms before Date parsing.
function formatBathymetryTime(value) {
    if (value == null || value === '') {
        return '';
    }
    const asNumber = Number(value);
    if (Number.isFinite(asNumber)) {
        return formatUserReportTime(asNumber);
    }
    return formatUserReportTime(value);
}

function closestPointOnBathymetryFeature(feature, lngLat) {
    const geom = feature?.geometry;
    if (!geom || (geom.type !== 'LineString' && geom.type !== 'MultiLineString')) {
        return null;
    }
    if (!Array.isArray(geom.coordinates) || !geom.coordinates.length) {
        return null;
    }
    try {
        const clickPt = turf.point([lngLat.lng, lngLat.lat]);
        const line = geom.type === 'LineString'
            ? turf.lineString(geom.coordinates)
            : turf.multiLineString(geom.coordinates);
        const nearest = turf.nearestPointOnLine(line, clickPt, { units: 'meters' });
        const coords = nearest?.geometry?.coordinates;
        if (!coords || coords.length < 2) {
            return null;
        }
        return {
            longitude: coords[0],
            latitude: coords[1],
            distanceM: Number(nearest.properties?.dist),
            depth: feature.properties?.depth,
            feature,
        };
    } catch {
        return null;
    }
}

function pickClosestBathymetryHit(features, lngLat) {
    let best = null;
    for (const feature of features) {
        if (!isBathymetryFeature(feature)) {
            continue;
        }
        const candidate = closestPointOnBathymetryFeature(feature, lngLat);
        if (!candidate || !Number.isFinite(candidate.distanceM)) {
            continue;
        }
        if (!best || candidate.distanceM < best.distanceM) {
            best = candidate;
        }
    }
    return best;
}

function createBathymetryPickElement() {
    const el = document.createElement('div');
    el.style.width = '8px';
    el.style.height = '8px';
    el.style.borderRadius = '50%';
    el.style.background = '#ffffff';
    el.style.boxShadow = '0px 0px 0px 2px rgba(0, 0, 0, 0.4)';
    el.style.boxSizing = 'border-box';
    // Clicks must pass through to the map so re-picks on the line still hit.
    el.style.pointerEvents = 'none';
    return el;
}

const DRAFT_SOURCE_ID = 'user-report-draft';
const DRAFT_LAYER_PREFIX = 'user-report-draft';
const HIT_QUERY_PAD_PX = 4;

// Owns map-side user report behavior: draft overlay, tile refresh, placement,
// click/hover for existing reports, and the report-issue map control.
export default function UserReportsLayer() {
    const {
        mapRef,
        style,
        reportIssueState,
        setReportIssueState,
        showUserReports,
        rulerControlRef,
    } = useContext(MapContext);
    const { forceReload } = useContext(UIContext);

    const isReportingIssue = reportIssueState != null;
    const reportIssueStateRef = useRef(reportIssueState);
    reportIssueStateRef.current = reportIssueState;

    const showUserReportsRef = useRef(showUserReports);
    showUserReportsRef.current = showUserReports;

    const styleRef = useRef(style);
    styleRef.current = style;

    const [userReportPopup, setUserReportPopup] = useState(null);
    const [bathymetryPopup, setBathymetryPopup] = useState(null);
    const reportIssueControlRef = useRef(null);
    const lastMousePos = useRef(null);
    const ensureUserReportDraftRef = useRef(null);
    const syncUserReportDraftRef = useRef(null);
    const refreshUserReportTilesRef = useRef(null);
    const syncPickMarkerRef = useRef(null);
    const bathymetryPickRef = useRef(null);
    const bathymetryPickMarkerRef = useRef(null);

    const refreshUserReportTiles = () => {
        reloadUserReportsSource(mapRef.current);
    };

    const applyUserReportStackTheme = (prefix, { opacity = 1 } = {}) => {
        if (!mapRef.current) {
            return;
        }
        const chartTheme = styleRef.current || 'day';
        const applyCirclePaint = (layerId, paint) => {
            if (!mapRef.current.getLayer(layerId)) {
                return;
            }
            Object.keys(paint).forEach((key) => {
                mapRef.current.setPaintProperty(layerId, key, paint[key]);
            });
        };
        const applyLabelStyle = (layerId) => {
            if (!mapRef.current.getLayer(layerId)) {
                return;
            }
            const layout = userReportLabelLayout(chartTheme);
            Object.keys(layout).forEach((key) => {
                mapRef.current.setLayoutProperty(layerId, key, layout[key]);
            });
            const paint = userReportLabelPaint(chartTheme, { opacity });
            Object.keys(paint).forEach((key) => {
                mapRef.current.setPaintProperty(layerId, key, paint[key]);
            });
        };
        applyCirclePaint(prefix + '-shadow', userReportShadowPaint({ opacity }));
        applyCirclePaint(prefix + '-outer', userReportOuterPaint({ opacity }));
        applyCirclePaint(prefix + '-white', userReportWhitePaint({ opacity, chartTheme }));
        applyCirclePaint(prefix + '-fill', userReportFillPaint({ opacity, chartTheme }));
        applyLabelStyle(prefix + '-1');
    };

    const ensureUserReportDraft = () => {
        if (!showUserReportsRef.current) {
            return null;
        }
        if (!mapRef.current || !mapRef.current.getSource) {
            return null;
        }
        if (!mapRef.current.isStyleLoaded()) {
            return null;
        }
        const chartTheme = styleRef.current || 'day';
        const draftOpacity = 0.5;
        const layout = userReportLabelLayout(chartTheme);
        const draftPaint = userReportLabelPaint(chartTheme, { opacity: draftOpacity });
        const draftLayers = [
            { id: DRAFT_LAYER_PREFIX + '-shadow', source: DRAFT_SOURCE_ID, type: 'circle', minzoom: USER_REPORT_MIN_ZOOM, paint: userReportShadowPaint({ opacity: draftOpacity }) },
            { id: DRAFT_LAYER_PREFIX + '-outer', source: DRAFT_SOURCE_ID, type: 'circle', minzoom: USER_REPORT_MIN_ZOOM, paint: userReportOuterPaint({ opacity: draftOpacity }) },
            { id: DRAFT_LAYER_PREFIX + '-white', source: DRAFT_SOURCE_ID, type: 'circle', minzoom: USER_REPORT_MIN_ZOOM, paint: userReportWhitePaint({ opacity: draftOpacity, chartTheme }) },
            { id: DRAFT_LAYER_PREFIX + '-fill', source: DRAFT_SOURCE_ID, type: 'circle', minzoom: USER_REPORT_MIN_ZOOM, paint: userReportFillPaint({ opacity: draftOpacity, chartTheme }) },
            { id: DRAFT_LAYER_PREFIX + '-1', source: DRAFT_SOURCE_ID, type: 'symbol', minzoom: USER_REPORT_MIN_ZOOM, layout, paint: draftPaint },
        ];
        if (!mapRef.current.getSource(DRAFT_SOURCE_ID)) {
            mapRef.current.addSource(DRAFT_SOURCE_ID, {
                type: 'geojson',
                data: draftReportToGeoJson(null),
            });
        }
        draftLayers.forEach((layer) => {
            if (!mapRef.current.getLayer(layer.id)) {
                mapRef.current.addLayer(layer);
            }
        });
        return mapRef.current.getSource(DRAFT_SOURCE_ID);
    };

    // Push draft GeoJSON to the map immediately. React effects alone can miss updates
    // when style reloads wipe the source between setState and the next effect run.
    const syncUserReportDraft = (draft = reportIssueStateRef.current) => {
        if (!showUserReportsRef.current) {
            return null;
        }
        const draftSource = ensureUserReportDraft();
        if (!draftSource) {
            return null;
        }
        draftSource.setData(draftReportToGeoJson(draft));
        mapRef.current?.triggerRepaint?.();
        return draftSource;
    };

    const removeBathymetryPickMarker = () => {
        if (bathymetryPickMarkerRef.current) {
            bathymetryPickMarkerRef.current.remove();
            bathymetryPickMarkerRef.current = null;
        }
    };

    const syncBathymetryPick = (point = bathymetryPickRef.current) => {
        const map = mapRef.current;
        if (!point) {
            removeBathymetryPickMarker();
            return null;
        }
        if (!map) {
            return null;
        }
        const lngLat = [point.longitude, point.latitude];
        if (bathymetryPickMarkerRef.current) {
            bathymetryPickMarkerRef.current.setLngLat(lngLat);
            return bathymetryPickMarkerRef.current;
        }
        bathymetryPickMarkerRef.current = new mapboxgl.Marker({
            element: createBathymetryPickElement(),
            anchor: 'center',
        })
            .setLngLat(lngLat)
            .addTo(map);
        return bathymetryPickMarkerRef.current;
    };

    ensureUserReportDraftRef.current = ensureUserReportDraft;
    syncUserReportDraftRef.current = syncUserReportDraft;
    refreshUserReportTilesRef.current = refreshUserReportTiles;
    syncPickMarkerRef.current = syncBathymetryPick;

    // Keep marker in sync with popup coords (backup if click-time sync was skipped).
    useEffect(() => {
        if (!bathymetryPopup) {
            bathymetryPickRef.current = null;
            syncPickMarkerRef.current?.(null);
            return;
        }
        const pick = {
            longitude: bathymetryPopup.longitude,
            latitude: bathymetryPopup.latitude,
        };
        bathymetryPickRef.current = pick;
        syncPickMarkerRef.current?.(pick);
    }, [bathymetryPopup?.longitude, bathymetryPopup?.latitude, bathymetryPopup?.id]);

    useEffect(() => {
        return () => {
            removeBathymetryPickMarker();
        };
    }, []);

    // Install report-issue control and rebuild draft after style reloads.
    useEffect(() => {
        let cancelled = false;
        let pollId = null;
        let attachedMap = null;

        const onMouseMove = (e) => {
            lastMousePos.current = {
                latitude: e.lngLat.lat,
                longitude: e.lngLat.lng,
            };
        };
        const onTouchMove = (e) => {
            lastMousePos.current = {
                latitude: e.lngLat.lat,
                longitude: e.lngLat.lng,
            };
        };

        const onStyleOrLoad = () => {
            if (!showUserReportsRef.current) {
                return;
            }
            syncUserReportDraftRef.current?.();
            syncPickMarkerRef.current?.(bathymetryPickRef.current);
            setTimeout(() => {
                refreshUserReportTilesRef.current?.();
            }, 100);
        };

        const ensureControl = (map) => {
            if (reportIssueControlRef.current) {
                return;
            }
            reportIssueControlRef.current = new ReportIssueControl({
                onToggle: (active) => {
                    if (active) {
                        if (rulerControlRef.current?.isMeasuring) {
                            rulerControlRef.current.measuringOff();
                        }
                        const center = map.getCenter();
                        const pos = lastMousePos.current;
                        setReportIssueState({
                            latitude: pos?.latitude ?? center.lat,
                            longitude: pos?.longitude ?? center.lng,
                            category: 'hazard',
                        });
                    } else {
                        setReportIssueState(null);
                    }
                },
            });
            map.addControl(reportIssueControlRef.current, 'bottom-right');
            // Keep the button below the rest of the bottom-right stack (same as
            // adding it first during Map init).
            const el = reportIssueControlRef.current.container;
            if (el?.parentNode?.firstChild !== el) {
                el.parentNode.insertBefore(el, el.parentNode.firstChild);
            }
            reportIssueControlRef.current.setVisible(showUserReportsRef.current);
            reportIssueControlRef.current.setActive(!!reportIssueStateRef.current);
        };

        const attach = (map) => {
            attachedMap = map;
            map.on('mousemove', onMouseMove);
            map.on('touchmove', onTouchMove);
            map.on('style.load', onStyleOrLoad);
            map.on('load', onStyleOrLoad);
            ensureControl(map);
            onStyleOrLoad();
        };

        const tryAttach = () => {
            if (cancelled || !mapRef.current) {
                return false;
            }
            attach(mapRef.current);
            return true;
        };

        if (!tryAttach()) {
            pollId = setInterval(() => {
                if (tryAttach() && pollId) {
                    clearInterval(pollId);
                    pollId = null;
                }
            }, 50);
        }

        return () => {
            cancelled = true;
            if (pollId) {
                clearInterval(pollId);
            }
            if (attachedMap) {
                attachedMap.off('mousemove', onMouseMove);
                attachedMap.off('touchmove', onTouchMove);
                attachedMap.off('style.load', onStyleOrLoad);
                attachedMap.off('load', onStyleOrLoad);
                if (reportIssueControlRef.current) {
                    try {
                        attachedMap.removeControl(reportIssueControlRef.current);
                    } catch (e) {
                        // Map may already be torn down.
                    }
                    reportIssueControlRef.current = null;
                }
            }
        };
    }, [mapRef, rulerControlRef, setReportIssueState]);

    useEffect(() => {
        reportIssueControlRef.current?.setVisible(showUserReports);
        if (!showUserReports) {
            if (reportIssueState) {
                setReportIssueState(null);
            }
            const draftSource = mapRef.current?.getSource(DRAFT_SOURCE_ID);
            if (draftSource) {
                draftSource.setData(draftReportToGeoJson(null));
            }
            return;
        }
        let cancelled = false;
        let pollId = null;
        const tryEnsure = () => {
            if (cancelled || !mapRef.current) {
                return false;
            }
            if (!mapRef.current.isStyleLoaded()) {
                return false;
            }
            ensureUserReportDraft();
            return true;
        };
        const startRefresh = () => {
            refreshUserReportTiles();
            syncUserReportDraft();
        };
        if (tryEnsure()) {
            startRefresh();
        } else {
            pollId = setInterval(() => {
                if (tryEnsure() && pollId) {
                    clearInterval(pollId);
                    pollId = null;
                    startRefresh();
                }
            }, 50);
        }
        const interval = setInterval(() => {
            refreshUserReportTiles();
        }, 60000);
        return () => {
            cancelled = true;
            if (pollId) {
                clearInterval(pollId);
            }
            clearInterval(interval);
        };
    }, [showUserReports]);

    useEffect(() => {
        reportIssueControlRef.current?.setActive(!!reportIssueState);
    }, [reportIssueState]);

    useEffect(() => {
        if (!mapRef.current) {
            return;
        }
        if (!showUserReports) {
            const draftSource = mapRef.current.getSource(DRAFT_SOURCE_ID);
            if (draftSource) {
                draftSource.setData(draftReportToGeoJson(null));
            }
            return;
        }
        // Retry briefly if the style is mid-reload when state changes.
        let cancelled = false;
        let pollId = null;
        const trySync = () => {
            if (cancelled) {
                return true;
            }
            return !!syncUserReportDraft(reportIssueState);
        };
        if (!trySync()) {
            pollId = setInterval(() => {
                if (trySync() && pollId) {
                    clearInterval(pollId);
                    pollId = null;
                }
            }, 50);
        }
        return () => {
            cancelled = true;
            if (pollId) {
                clearInterval(pollId);
            }
        };
    }, [reportIssueState, forceReload, showUserReports]);

    useEffect(() => {
        if (!mapRef.current || !showUserReports) {
            return;
        }
        syncUserReportDraft();
        applyUserReportStackTheme(DRAFT_LAYER_PREFIX, { opacity: 0.5 });
        setTimeout(() => {
            refreshUserReportTiles();
        }, 100);
    }, [style, forceReload, showUserReports]);

    useEffect(() => {
        if (!isReportingIssue || !showUserReports) {
            if (mapRef.current) {
                mapRef.current.getCanvas().style.cursor = '';
            }
            return;
        }

        setUserReportPopup(null);
        setBathymetryPopup(null);

        let cancelled = false;
        let pollId = null;
        let attachedMap = null;
        let downPoint = null;
        let lastPlaceMs = 0;

        const placeAt = (lngLat) => {
            if (!lngLat) {
                return;
            }
            const now = Date.now();
            // mouseup + click can both fire; ignore the duplicate.
            if (now - lastPlaceMs < 80) {
                return;
            }
            lastPlaceMs = now;
            setReportIssueState((old) => {
                if (!old) {
                    return old;
                }
                const next = {
                    ...old,
                    latitude: lngLat.lat,
                    longitude: lngLat.lng,
                };
                reportIssueStateRef.current = next;
                // Update the GeoJSON source now — don't wait for a React effect.
                // Effects can miss when a style reload recreates the source empty.
                syncUserReportDraftRef.current?.(next);
                return next;
            });
        };

        // Mapbox suppresses "click" if the pointer moves a few pixels (treats it as a
        // drag). Use mouseup/touchend with a looser threshold so placement feels reliable.
        const onPointerDown = (event) => {
            const button = event.originalEvent?.button;
            if (button != null && button !== 0) {
                downPoint = null;
                return;
            }
            downPoint = event.point || null;
        };

        const onPointerUp = (event) => {
            const button = event.originalEvent?.button;
            if (button != null && button !== 0) {
                downPoint = null;
                return;
            }
            if (!event?.lngLat) {
                downPoint = null;
                return;
            }
            if (downPoint && event.point) {
                const dx = event.point.x - downPoint.x;
                const dy = event.point.y - downPoint.y;
                downPoint = null;
                // ~12px tolerance — more forgiving than Mapbox's default click filter.
                if ((dx * dx) + (dy * dy) > 144) {
                    return;
                }
            } else {
                downPoint = null;
            }
            placeAt(event.lngLat);
        };

        const onClick = (event) => {
            placeAt(event?.lngLat);
        };

        const attach = (map) => {
            attachedMap = map;
            map.getCanvas().style.cursor = 'crosshair';
            map.on('mousedown', onPointerDown);
            map.on('touchstart', onPointerDown);
            map.on('mouseup', onPointerUp);
            map.on('touchend', onPointerUp);
            map.on('click', onClick);
        };

        const tryAttach = () => {
            if (cancelled || !mapRef.current) {
                return false;
            }
            attach(mapRef.current);
            return true;
        };

        if (!tryAttach()) {
            pollId = setInterval(() => {
                if (tryAttach() && pollId) {
                    clearInterval(pollId);
                    pollId = null;
                }
            }, 50);
        }

        return () => {
            cancelled = true;
            if (pollId) {
                clearInterval(pollId);
            }
            if (attachedMap) {
                attachedMap.off('mousedown', onPointerDown);
                attachedMap.off('touchstart', onPointerDown);
                attachedMap.off('mouseup', onPointerUp);
                attachedMap.off('touchend', onPointerUp);
                attachedMap.off('click', onClick);
                if (attachedMap.getCanvas()) {
                    attachedMap.getCanvas().style.cursor = '';
                }
            }
        };
    }, [isReportingIssue, showUserReports, setReportIssueState]);

    useEffect(() => {
        if (!showUserReports || isReportingIssue) {
            setUserReportPopup(null);
            setBathymetryPopup(null);
            return;
        }

        const queryHitFeatures = (map, point) => {
            const layers = HIT_LAYER_IDS.filter((id) => map.getLayer(id));
            if (!layers.length) {
                return [];
            }
            // Pad the query so clicks on the painted line border still hit.
            const pad = HIT_QUERY_PAD_PX;
            const box = [
                [point.x - pad, point.y - pad],
                [point.x + pad, point.y + pad],
            ];
            return map.queryRenderedFeatures(box, { layers });
        };

        const openUserReportPopup = (feature, lngLat) => {
            const props = feature.properties || {};
            const reportType = props.report_type || props.reportType;
            const typeLabel = props.typeLabel
                || REPORT_CATEGORY_BY_ID[reportType]?.label
                || reportType
                || 'Report';
            const reportedAt = props.reportedAt
                || formatUserReportTime(props.createdAt)
                || (typeof props.label === 'string' && props.label.includes(' - ')
                    ? props.label.split(' - ').slice(1).join(' - ')
                    : '');
            const coords = feature.geometry?.coordinates;
            const isOwner = !!(
                props.is_owner === true
                || props.is_owner === 1
                || props.is_owner === 'true'
                || props.isOwner === true
            );
            setBathymetryPopup(null);
            setUserReportPopup({
                id: props.id,
                longitude: coords?.[0] ?? lngLat.lng,
                latitude: coords?.[1] ?? lngLat.lat,
                reportType,
                typeLabel,
                reportedAt,
                description: getUserReportDescription(props),
                iconSrc: REPORT_CATEGORY_BY_ID[reportType]?.iconSrc,
                isOwner,
                upvoteCount: Number(props.upvote_count ?? props.upvoteCount) || 0,
                downvoteCount: Number(props.downvote_count ?? props.downvoteCount) || 0,
            });
        };

        const openBathymetryPopup = (features, lngLat) => {
            const closest = pickClosestBathymetryHit(features, lngLat);
            const feature = closest?.feature || features.find(isBathymetryFeature) || features[0];
            const props = feature?.properties || {};
            const createdAt = props.created_at ?? props.createdAt;
            const startTime = props.start_time ?? props.startTime;
            const reportedAt = formatBathymetryTime(createdAt)
                || formatBathymetryTime(startTime)
                || '';
            const platformRaw = props.platform_name || props.platformName || '';
            const platformName = platformRaw ? String(platformRaw) : '';
            const longitude = closest?.longitude ?? lngLat.lng;
            const latitude = closest?.latitude ?? lngLat.lat;
            const depthRaw = closest?.depth ?? props.depth;
            const pick = { longitude, latitude };
            bathymetryPickRef.current = pick;
            // Sync immediately — Marker.setLngLat is reliable across re-clicks.
            syncPickMarkerRef.current?.(pick);
            setUserReportPopup(null);
            setBathymetryPopup({
                id: props.id,
                longitude,
                latitude,
                reportedAt,
                // Store meters; popup formats with MapContext depthUnits.
                depthM: parseDepthMeters(depthRaw),
                minDepthM: parseDepthMeters(props.min_depth ?? props.minDepth),
                maxDepthM: parseDepthMeters(props.max_depth ?? props.maxDepth),
                lengthLabel: formatLengthMeters(props.length_m ?? props.lengthM),
                platformName,
            });
        };

        const onClick = (event) => {
            const map = mapRef.current;
            if (!map) {
                return;
            }
            // Measure tool owns clicks while active — don't open report popups.
            if (rulerControlRef.current?.isMeasuring) {
                setUserReportPopup(null);
                setBathymetryPopup(null);
                return;
            }
            const features = queryHitFeatures(map, event.point);
            if (!features.length) {
                setUserReportPopup(null);
                setBathymetryPopup(null);
                return;
            }
            // queryRenderedFeatures returns topmost paint order first.
            const top = features[0];
            if (isBathymetryFeature(top)) {
                openBathymetryPopup(features, event.lngLat);
                return;
            }
            openUserReportPopup(top, event.lngLat);
        };

        const onMouseMove = (event) => {
            const map = mapRef.current;
            if (!map) {
                return;
            }
            // Measure mode owns the map cursor (pointer / grab on points).
            if (rulerControlRef.current?.isMeasuring) {
                return;
            }
            const features = queryHitFeatures(map, event.point);
            map.getCanvas().style.cursor = features.length ? 'pointer' : '';
        };

        let cancelled = false;
        let pollId = null;
        let attachedMap = null;

        const attach = (map) => {
            attachedMap = map;
            map.on('click', onClick);
            map.on('mousemove', onMouseMove);
        };

        const tryAttach = () => {
            if (cancelled || !mapRef.current) {
                return false;
            }
            attach(mapRef.current);
            return true;
        };

        if (!tryAttach()) {
            pollId = setInterval(() => {
                if (tryAttach() && pollId) {
                    clearInterval(pollId);
                    pollId = null;
                }
            }, 50);
        }

        return () => {
            cancelled = true;
            if (pollId) {
                clearInterval(pollId);
            }
            if (attachedMap) {
                attachedMap.off('click', onClick);
                attachedMap.off('mousemove', onMouseMove);
                if (attachedMap.getCanvas()) {
                    attachedMap.getCanvas().style.cursor = '';
                }
            }
            setUserReportPopup(null);
            setBathymetryPopup(null);
        };
    }, [showUserReports, isReportingIssue, forceReload, style]);

    return (
        <>
            <UserReportPopup
                popup={userReportPopup}
                onClose={() => setUserReportPopup(null)}
                onVoted={(updated) => {
                    setUserReportPopup((old) => old && old.id === updated.id ? {
                        ...old,
                        upvoteCount: updated.upvoteCount || 0,
                        downvoteCount: updated.downvoteCount || 0,
                    } : old);
                    refreshUserReportTiles();
                }}
                onDeleted={() => {
                    setUserReportPopup(null);
                    refreshUserReportTiles();
                }}
            />
            <UserReportedBathymetryPopup
                popup={bathymetryPopup}
                onClose={() => setBathymetryPopup(null)}
            />
        </>
    );
}
